import { CloseIcon } from 'assets/icons';
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

const ContainerCloseIcon = styled(CloseIcon)`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 16px;
  height: 16px;
  cursor: pointer;
`;

interface MessageCategorySelectorProps {
  onSelect: (arg0: string) => void;
  onClose?: () => void;
}

const MessageCategorySelector = ({ onSelect, onClose }: MessageCategorySelectorProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  /* call onClose if the user clicked outside this component */
  const onClickOutside = (e: Event) => {
    if (!onClose || !wrapperRef?.current || !e.target) return;
    if (wrapperRef.current.contains(e.target as Node)) return;

    onClose();
  };

  useEffect(() => {
    document.addEventListener('click', onClickOutside, false);
    return () => {
      document.removeEventListener('click', onClickOutside, false);
    };
  }, []);

  return (
    <Container ref={wrapperRef}>
      {onClose && <ContainerCloseIcon onClick={onClose} />}
      <Header>Select a message category:</Header>
      <ul>
        <CategoryOption onClick={() => onSelect('admin')}>
          <strong>Admin team</strong>
          <Description>Scheduling, Records, Referrals</Description>
        </CategoryOption>
        <CategoryOption onClick={() => onSelect('medical')}>
          <strong>Medical team</strong>
          <Description>Medications, Care Plan, Results</Description>
        </CategoryOption>
        <CategoryOption onClick={() => onSelect('billing')}>
          <strong>Billing team</strong>
          <Description>Billing and insurance issues or questions</Description>
        </CategoryOption>
        <CategoryOption onClick={() => onSelect('tech')}>
          <strong>Tech support</strong>
          <Description>Technical issues with portal or app</Description>
        </CategoryOption>
      </ul>
    </Container>
  );
};

export default MessageCategorySelector;

const Container = styled.div`
  ${({ theme }) => theme.fonts.FoundersGrotesk};
  background: white;
  box-shadow: -4px 4px 0 0 ${({ theme }) => theme.colors.night};
  border: solid 2px ${({ theme }) => theme.colors.night};
  padding: 20px;
  position: absolute;
  z-index: 500;
  width: 95%;
  right: 2.5%;

  @media (min-width: ${({ theme }) => theme.breakpoints.tabletPortrait}) {
    width: 350px;
    right: 50px;
    top: 20px;
  }
`;

const Header = styled.h3`
  font-weight: bold;
  margin-bottom: 20px;
`;

const CategoryOption = styled.li`
  background-color: ${({ theme }) => theme.colors.yellowLight};
  padding: 1em;
  margin: 10px 0;
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.colors.yellow};
  }
`;

const Description = styled.div`
  font-size: 0.85em;
`;
